import { useMemo } from 'react';
import { StopAreaApiModel } from '../models/stop-area.api-model';
import { ConfigurationService } from '../../../services/configuration.service';
import { AnalyticsService } from '../../../services/analytics-service';
import { TranslationService } from '../../../services/translation.service';
import DepartureBoardLinkHelper from '../../../helpers/departure-board-link.helper';
import OverlayBox from '../../Shared/OverlayBox';
import StopAreaMap from '../StopAreaMap';
import type { IStopArea } from '../../../models/stop-area.model';
import './style.scss';
import StopAreaMapHelper from '../StopAreaMap/helpers/stop-area-map.helper';
import useFetch from '../../../hooks/fetch.hook';
import { CommuterParkingLotApiModel } from '../models/commuter-parking-lot.api-model';
import { TicketOfficeApiModel } from '../models/ticket-office.api.model';
import { ICommuterParkingLot } from '../../../models/commuter-parking-lot.model';
import { ITicketOffice } from '../../../models/ticket-office.model';
import { DistanceHelper } from '../../../helpers/distance.helper';
import { StopPointType } from '../../../models/stop-point-type.enum';
import { useTexts } from '../../../hooks/texts.hook';

const MAX_DISTANCE_METERS = 500;

function processReceivedStopAreaData(data: StopAreaApiModel | undefined): IStopArea | undefined {
  return data ? StopAreaApiModel.mapToBusinessModel(data) : undefined;
}

function processReceivedCommuterParkingLotsData(data: CommuterParkingLotApiModel[] | undefined): ICommuterParkingLot[] {
  return data?.map(CommuterParkingLotApiModel.mapToBusinessModel) ?? [];
}

function processReceivedTicketOfficesData(data: TicketOfficeApiModel[] | undefined): ITicketOffice[] {
  return data?.map(TicketOfficeApiModel.mapToBusinessModel) ?? [];
}

function StopAreaSearchDetailsComponent() {
  // === CONSTANTS ===
  const stopAreaGid = ConfigurationService.get('stopareasearchpage.stopareagid');
  const departureBoardUrl = stopAreaGid ? DepartureBoardLinkHelper.generateLink([stopAreaGid], undefined, 'vasttrafikse-stopareadetailspage') : undefined;
  const showCommuterParkingLots = new URLSearchParams(window.location.search).get('showCommuterParkingLots')?.toLocaleLowerCase() === 'true';
  const showTicketOffices = new URLSearchParams(window.location.search).get('showTicketOffices')?.toLocaleLowerCase() === 'true';

  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const [{ data: getStopAreaData }] = useFetch<StopAreaApiModel>(`/api/stop-area-search/stopareas/${stopAreaGid}`);
  const [{ data: getCommuterParkingLotsData }] = useFetch<CommuterParkingLotApiModel[]>('/api/stop-area-search/commuterparkinglots', !showCommuterParkingLots);
  const [{ data: getTicketOfficesData }] = useFetch<TicketOfficeApiModel[]>('/api/stop-area-search/ticketoffices', !showTicketOffices);

  // === VARIABLES ===
  const stopArea = processReceivedStopAreaData(getStopAreaData);
  const commuterParkingLots = useMemo(() => {
    if (!stopArea || !showCommuterParkingLots || !stopArea.latitude || !stopArea.longitude) return [];
    return processReceivedCommuterParkingLotsData(getCommuterParkingLotsData)
      .filter((x) => x.latitude && x.longitude)
      .filter((x) => DistanceHelper.calculateDistanceInMeters(stopArea.latitude!, stopArea.longitude!, x.latitude, x.longitude) <= MAX_DISTANCE_METERS);
  }, [getCommuterParkingLotsData, showCommuterParkingLots, stopArea]);

  const ticketOffices = useMemo(() => {
    if (!stopArea || !showTicketOffices || !stopArea.latitude || !stopArea.longitude) return [];
    return processReceivedTicketOfficesData(getTicketOfficesData)
      .filter((x) => x.latitude && x.longitude)
      .filter((x) => DistanceHelper.calculateDistanceInMeters(stopArea.latitude!, stopArea.longitude!, x.latitude, x.longitude) <= MAX_DISTANCE_METERS);
  }, [getTicketOfficesData, showTicketOffices, stopArea]);

  const overlayContentText = useMemo(() => {
    const trainStops: string[] = [];
    const platForms: string[] = [];

    stopArea?.stopPoints?.forEach((stopPoint) => {
      if (stopPoint.stopPointType === StopPointType.trainStop) {
        if (stopPoint.platform) trainStops.push(stopPoint.platform);
      } else {
        if (stopPoint.platform) platForms.push(stopPoint.platform);
      }
    });

    const trainStopsText = trainStops.length > 0 ? `${t.track.toLocaleLowerCase()} ${trainStops.join(', ')}` : undefined;
    const platFormsText = platForms.length > 0 ? `${t.platform.toLocaleLowerCase()} ${platForms.join(', ')}` : undefined;

    return TranslationService.get('stopareasearchpage.prefixoverlayboxcontenttext').replace(
      '{0}',
      `${TranslationService.get('stopareasearchpage.stopareaname')} ${trainStopsText ? `${trainStopsText}${platFormsText ? ` ${t.and} ` : ''}` : ''}${platFormsText ? platFormsText : ''}`,
    );
  }, [stopArea]);

  function handleOnDepartureLinkClicked() {
    AnalyticsService.addEvent(AnalyticsService.StopArea, 'Detaljsida - klick på länk till avgångstavla', '');
  }

  function handleOnOverlayBoxLoad() {
    StopAreaMapHelper.moveBreadcrumbs('stopareasearchpage-breadcrumbs');
  }

  return (
    <div className="stop-area-search-details">
      <OverlayBox
        headerText={TranslationService.get('stopareasearchpage.stopareaheader')}
        contentText={overlayContentText}
        contentImageUrl={ConfigurationService.get('stopareasearchpage.overlayboxcontentimageurl')}
        contentImageAltText={TranslationService.get('stopareasearchpage.overlayboxcontentimagealttext')}
        externalLinkText={TranslationService.get('stopareasearchpage.departureboardlinktext')}
        externalLinkUrl={departureBoardUrl}
        onExternalLinkClick={handleOnDepartureLinkClicked}
        onLoad={handleOnOverlayBoxLoad}
      />
      <StopAreaMap mapClassName="stop-area-search-details" stopPoints={stopArea?.stopPoints} commuterParkingLots={commuterParkingLots} ticketOffices={ticketOffices} />
    </div>
  );
}

export default StopAreaSearchDetailsComponent;
